import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Badge, Button, Card, CardHeader, Tabs } from '../components/ui.js'
import { cn } from '../lib/cn.js'
import { useTRPC } from '../lib/trpc.js'
import { CertificationsView } from './settings/CertificationsView.js'
import { InviteDialog } from './settings/InviteDialog.js'
import { PeopleView } from './settings/PeopleView.js'

/**
 * Settings → People, on the dispatch board's surface.
 *
 * One board card holding two views behind a segmented pill: the list of people,
 * and every ticket in the business. They are one screen rather than two settings
 * sections because the questions run together — you notice an expiry on the
 * compliance view and the next thing you want is that person's record.
 *
 * A person's own page is a route, `/people/:userId`, and not a popup. Rule 13 in
 * AGENTS.md is about jobs: a job is glanced at over whatever you were doing, and
 * a person is a record you sit with.
 */

type View = 'people' | 'certifications'

export function UsersPanel() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [inviting, setInviting] = useState(false)

  const view: View = searchParams.get('view') === 'certifications' ? 'certifications' : 'people'

  const setView = (next: View) => {
    const params = new URLSearchParams(searchParams)
    if (next === 'people') params.delete('view')
    else params.set('view', next)
    setSearchParams(params, { replace: true })
  }

  return (
    <>
      <Card variant="board">
        <div className="flex flex-wrap items-end justify-between gap-3 px-[var(--row-pad-x)] pt-3.5 pb-3">
          <div className="min-w-0">
            <h1 className="font-display text-xl font-extrabold text-brand-800">People</h1>
            <Subtitle view={view} />
          </div>

          <Button variant="primary" className="rounded-full" onClick={() => setInviting(true)}>
            New person
          </Button>
        </div>

        <ViewTabs view={view} onChange={setView} />

        {view === 'people' ? (
          <PeopleView onInvite={() => setInviting(true)} />
        ) : (
          <CertificationsView />
        )}
      </Card>

      <RolesCard />

      <InviteDialog open={inviting} onClose={() => setInviting(false)} />
    </>
  )
}

function Subtitle({ view }: { view: View }) {
  const trpc = useTRPC()
  const users = useQuery(trpc.users.list.queryOptions({ includeArchived: false }))
  const overview = useQuery({
    ...trpc.licences.overview.queryOptions(),
    enabled: view === 'certifications',
  })

  if (view === 'certifications') {
    if (!overview.data) return null
    // The same three the grid's rows count: expired, undated, and a gap.
    const { expired, undated, missing } = overview.data.counts
    const needing = expired + undated + missing
    return (
      <p className="mt-0.5 text-xs text-ink-500">
        {needing === 0
          ? 'Every ticket is in order'
          : `${needing} ${needing === 1 ? 'ticket needs' : 'tickets need'} attention`}
      </p>
    )
  }

  if (!users.data) return null

  const scheduled = users.data.filter((user) => user.isSchedulable).length
  return (
    <p className="mt-0.5 text-xs text-ink-500">
      {users.data.length} {users.data.length === 1 ? 'person' : 'people'} · {scheduled} on the board
    </p>
  )
}

/** The job card's tabs: the people, or every ticket they hold. */
function ViewTabs({ view, onChange }: { view: View; onChange: (view: View) => void }) {
  return (
    <Tabs
      label="People view"
      value={view}
      onChange={onChange}
      options={[
        { value: 'people', label: 'People', title: 'Everyone, their teams and their roles' },
        {
          value: 'certifications',
          label: 'Certifications',
          title: 'Every ticket in the business, soonest expiry first',
        },
      ]}
    />
  )
}

function RolesCard() {
  const trpc = useTRPC()
  const roles = useQuery(trpc.users.roles.queryOptions())

  return (
    <Card>
      <CardHeader title="Roles" />
      <ul className="divide-y divide-ink-100">
        {(roles.data ?? []).map((role) => (
          <li
            key={role.id}
            className="flex items-center gap-3 px-[var(--row-pad-x)] py-[var(--row-pad-y)]"
          >
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2">
                <span className="text-sm font-medium text-ink-900">{role.name}</span>
                {role.isSystem ? <Badge>Built in</Badge> : null}
              </span>
              <span className="block text-xs text-ink-500">{role.description}</span>
            </span>
            <span className="shrink-0 text-xs tabular-nums text-ink-500">
              {role.permissions.length} permissions · {role._count.users} people
            </span>
          </li>
        ))}
      </ul>
    </Card>
  )
}
